import React, { useEffect } from "react";
import { IZAddSupplierProps, SupplierFormValues } from "./interface";
import ZDrawer from "@molecules/ZDrawer";
import {
	IFormProps,
	IFormSectionProps,
	IFormFieldProps,
	EWidgets
} from "@organisms/ZForm/interface";
import Form from "antd/lib/form";
import Divider from "antd/lib/divider";
import { ZTypography, ZIcon, ZTooltip } from "@atoms";
import { EFontWeights } from "@atoms/ZTypography/interface";
import { ZForm } from "@organisms";
import { GlobalState } from "@/Reducers/interface";
import { normalizeZipcode } from "@/V2Utils";
import { EIcons } from "@atoms/ZIcon/interface";
import { useSelector } from "react-redux";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import SettingsActionCreators from "@/Actions/Settings";
import useDispatch from "@/Components/Utils/useDispatch";
import { FormKeys } from "@/Contracts";

const ZAddOrEditSupplier = (props: IZAddSupplierProps) => {
	const currentActiveSupplier = useSelector((state: GlobalState) =>
		state.settings.suppliers.find(
			supplier => supplier.id === props.currentSupplierId
		)
	);

	const { isFormEdited } = useSelector((state: GlobalState) => ({
		isFormEdited: state.settings.isFormEdited
	}));

	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const handleClose = () => {
		form.resetFields();
		props.closeDrawer();
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddEditSupplier)
		);
	};

	const handleCancel = () => {
		if (!isFormEdited?.[FormKeys.AddEditSupplier]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					!!props.currentSupplierId
						? "editing supplier"
						: "adding supplier"
				}?`,
				onOk: handleClose
			});
		}
	};

	const handleOnSubmit = async (formValues: SupplierFormValues) => {
		if (props.currentSupplierId) {
			const editedSupplier = await props.editSupplier?.(formValues);
			if (!editedSupplier) return;
		} else {
			const addedSupplier = await props.addSupplier(formValues);
			if (!addedSupplier) return;
			addedSupplier && form.resetFields();
		}

		await props.fetchSuppliersWithPagination?.();
		handleClose();
	};

	useEffect(() => {
		if (currentActiveSupplier) {
			form.setFieldsValue({
				supplier_name: currentActiveSupplier?.name,
				has_drop_ship: currentActiveSupplier?.has_drop_ship,
				address_line_1: currentActiveSupplier?.address?.address_line_1,
				address_line_2: currentActiveSupplier?.address?.address_line_2,
				state: currentActiveSupplier?.address?.state,
				country: currentActiveSupplier?.address?.country,
				city: currentActiveSupplier?.address?.city,
				email: currentActiveSupplier?.email,
				zip_code: currentActiveSupplier?.address?.pincode
			});
		}
	}, [currentActiveSupplier]);

	const fields: IFormFieldProps[] = [
		{
			key: "general_info",
			render: () => (
				<div className="my-4">
					<ZTypography.ZText weight={EFontWeights.MEDIUM}>
						GENERAL INFO
					</ZTypography.ZText>
				</div>
			)
		},
		{
			key: "supplier_name",
			label: "Supplier Name",
			required: true,
			message: "Please enter supplier name",
			hasFeedback: true,
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter supplier name"
			},
			rules: [
				{ whitespace: true, message: "Please enter supplier name" }
			],
			disabled: currentActiveSupplier?.is_system_generated
		},
		{
			key: "email",
			label: "Email",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Please enter email",
				autoComplete: "section-primary email",
				disabled: false
			},
			required: false,
			message: "Please enter valid email",
			rules: [
				{
					transform: (v: string | null) => v && v.trim(),
					type: "email",
					message: "Please enter email address in valid format"
				}
			]
		},
		{
			key: "divider",
			render: () => <Divider />
		},
		{
			key: "address",
			render: () => (
				<div className="mt-6 mb-5">
					<ZTypography.ZText weight={EFontWeights.MEDIUM}>
						Address
					</ZTypography.ZText>
				</div>
			)
		},
		{
			key: "address_line_1",
			label: "Street",
			required: false,
			message: `Please enter street`,
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: `Enter Street`
			},
			rules: [
				{
					whitespace: true,
					message: `Please enter street`
				}
			]
		},
		{
			key: "address_line_2",
			label: "Street 2",
			required: false,
			message: "Please enter street 2",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Enter Street 2"
			},
			rules: [
				{
					whitespace: true,
					message: "Please enter street 2"
				}
			]
		},
		{
			key: "city",
			label: "City",
			required: false,
			message: "Please enter city",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Enter City"
			},
			rules: [{ whitespace: true, message: "Please enter city" }]
		},
		{
			key: "state",
			label: "State",
			required: false,
			message: "Please enter state",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Enter State"
			},
			rules: [{ whitespace: true, message: "Please enter state" }]
		},
		{
			key: "zip_code",
			label: "Zip Code",
			required: false,
			message: "Please enter zip code",
			normalize: normalizeZipcode,
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Enter Zip Code"
			},
			rules: [{ whitespace: true, message: "Please enter zip code" }]
		},
		{
			key: "country",
			label: "Country",
			required: false,
			message: "Please enter country",
			widget: EWidgets.INPUT,
			widgetProps: {
				width: "full",
				placeholder: "Enter Country"
			},
			rules: [{ whitespace: true, message: "Please enter country" }]
		},
		{
			key: "divider_two",
			render: () => <Divider />
		},
		{
			key: "has_drop_ship",
			label: (
				<div className="flex items-center">
					<ZTypography.ZText className="mr-2">
						Drop Ship
					</ZTypography.ZText>
					<ZTooltip
						title="Can the supplier ship items directly to client?"
						placement="top"
						className="max-w-70"
					>
						<ZIcon name={EIcons.WARNINGCIRCLE} size="sm" />
					</ZTooltip>
				</div>
			),
			className: "my-6 items-center",
			widget: EWidgets.SWITCH,
			hasFeedback: true,
			widgetProps: {
				size: "small"
			},
			initialValue: true,
			disabled: currentActiveSupplier?.is_system_generated
		},
		{
			key: "divider_three",
			render: () => <Divider />
		}
	];

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			meta: {
				formItemLayout: [5, 19],
				fields
			}
		}
	];

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditSupplier] &&
			dispatch(
				SettingsActionCreators.setFormEdited(FormKeys.AddEditSupplier)
			);
	};

	const formProps: IFormProps = {
		onFinish: handleOnSubmit,
		key: "AddOrEditSupplier",
		sections,
		loading: props.isFormLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: handleCancel,
		isRightFooter: true,
		shouldDisableBeforeTouch: false,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleCancel}
			title={!!props.currentSupplierId ? "Edit Supplier" : "Add Supplier"}
			removeDefaultPadding={true}
		>
			<ZForm {...formProps} className="py-4" />
		</ZDrawer>
	);
};

export default ZAddOrEditSupplier;
